import React from 'react';
import { Plus, BracketsSquare, X } from '@phosphor-icons/react';
import {
  LogicalOperator,
  FilterOperator,
  UnitFilterCondition,
  UnitFilterGroup,
  FilterableFieldConfig,
  isUnitFilterGroup,
} from 'shared/types/cluster';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { UnitFilterConditionRow } from './UnitFilterConditionRow';

interface UnitFilterGroupEditorProps {
  group: UnitFilterGroup;
  fieldConfigs: FilterableFieldConfig[];
  onChange: (updated: UnitFilterGroup) => void;
  onRemove?: () => void;
  depth?: number;
}

export const UnitFilterGroupEditor: React.FC<UnitFilterGroupEditorProps> = ({
  group,
  fieldConfigs,
  onChange,
  onRemove,
  depth = 0,
}) => {
  const isAnd = group.logicalOperator === LogicalOperator.AND;

  const toggleOperator = () => {
    onChange({
      ...group,
      logicalOperator: isAnd ? LogicalOperator.OR : LogicalOperator.AND,
    });
  };

  const handleConditionChange = (
    index: number,
    updated: UnitFilterCondition | UnitFilterGroup,
  ) => {
    const newConditions = [...group.conditions];
    newConditions[index] = updated;
    onChange({ ...group, conditions: newConditions });
  };

  const handleRemoveCondition = (index: number) => {
    const newConditions = group.conditions.filter((_, i) => i !== index);
    onChange({ ...group, conditions: newConditions });
  };

  const addCondition = () => {
    const defaultField = fieldConfigs[0]?.field || '';
    const defaultOperator =
      fieldConfigs[0]?.operators[0] || FilterOperator.EQUALS;
    const newCondition: UnitFilterCondition = {
      id: crypto.randomUUID(),
      field: defaultField,
      operator: defaultOperator,
      value: null,
    };
    onChange({
      ...group,
      conditions: [...group.conditions, newCondition],
    });
  };

  const addNestedGroup = () => {
    const newGroup: UnitFilterGroup = {
      id: crypto.randomUUID(),
      logicalOperator: LogicalOperator.AND,
      conditions: [],
    };
    onChange({
      ...group,
      conditions: [...group.conditions, newGroup],
    });
  };

  const borderColor = depth === 0
    ? 'border-moss-primary/30'
    : depth === 1
      ? 'border-brick-primary/20'
      : 'border-border';

  return (
    <div
      className={cn(
        'rounded-lg border-2 p-3 space-y-3',
        borderColor,
        depth === 0 && 'bg-card',
        depth > 0 && 'bg-muted/30',
      )}
    >
      {/* Group header with AND/OR toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleOperator}
            className={cn(
              'px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide transition-colors',
              'border cursor-pointer',
              isAnd
                ? 'bg-moss-primary/10 text-moss-primary border-moss-primary/30 hover:bg-moss-primary/20'
                : 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100',
            )}
          >
            {isAnd ? 'EN' : 'OF'}
          </button>
          <span className="text-xs text-muted-foreground">
            {isAnd
              ? 'Alle voorwaarden moeten kloppen'
              : 'Minstens 1 voorwaarde moet kloppen'}
          </span>
        </div>
        {onRemove && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="text-muted-foreground hover:text-destructive h-7 w-7"
          >
            <X className="size-4" />
          </Button>
        )}
      </div>

      {/* Conditions list */}
      {group.conditions.length === 0 ? (
        <div className="py-4 text-center text-sm text-muted-foreground rounded-lg border border-dashed">
          Geen voorwaarden. Voeg een voorwaarde toe.
        </div>
      ) : (
        <div className="space-y-2">
          {group.conditions.map((item, index) => (
            <div key={isUnitFilterGroup(item) ? item.id || index : item.id || index}>
              {/* Show AND/OR label between items */}
              {index > 0 && (
                <div className="flex items-center gap-2 py-1">
                  <div className="flex-1 h-px bg-border" />
                  <Badge
                    variant="secondary"
                    className={cn(
                      'text-[10px] uppercase',
                      isAnd ? 'text-moss-primary' : 'text-amber-700',
                    )}
                  >
                    {isAnd ? 'EN' : 'OF'}
                  </Badge>
                  <div className="flex-1 h-px bg-border" />
                </div>
              )}

              {isUnitFilterGroup(item) ? (
                <UnitFilterGroupEditor
                  group={item}
                  fieldConfigs={fieldConfigs}
                  onChange={(updated) => handleConditionChange(index, updated)}
                  onRemove={() => handleRemoveCondition(index)}
                  depth={depth + 1}
                />
              ) : (
                <UnitFilterConditionRow
                  condition={item}
                  fieldConfigs={fieldConfigs}
                  onChange={(updated) => handleConditionChange(index, updated)}
                  onRemove={() => handleRemoveCondition(index)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add buttons */}
      <div className="flex items-center gap-2 pt-1">
        <Button
          variant="outline"
          size="sm"
          onClick={addCondition}
          className="text-xs"
        >
          <Plus className="size-3 mr-1" />
          Voorwaarde
        </Button>
        {depth < 2 && (
          <Button
            variant="outline"
            size="sm"
            onClick={addNestedGroup}
            className="text-xs"
          >
            <BracketsSquare className="size-3 mr-1" />
            Groep
          </Button>
        )}
      </div>
    </div>
  );
};

export default UnitFilterGroupEditor;
